import { Home } from '@mui/icons-material';
import { Box, Button, Container, Paper, Fade } from '@mui/material';
import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router';
import MissingPaymentForm from '@acorn-portal/shared/src/components/forms/MissingPaymentForm';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import PageTitle from '@acorn-portal/shared/src/components/text/PageTitle';
import usePayNowDetails from '@acorn-portal/shared/src/models/paynow-model';
import { PayNowClient, PayNowStatus } from '@acorn-portal/shared/src/services/api';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { updatePayNowDetails } from '@acorn-portal/shared/src/store/paynow.slice';
import FullPageLoadingSplash from '@acorn-portal/shared/src/components/loading/FullPageLoadingSplash';
import { useAuth } from 'react-oidc-context';

const SplitPaymentAgreement: FC = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const auth = useAuth();
	const customerEmail = auth.user?.profile.email;
	const policyDetails = useAppSelector((redux) => redux.fullPolicy);
	const { payNowDetails } = usePayNowDetails(policyDetails.policyNumber);
	const { details, collectionOptions } = payNowDetails;

	const hasOverDuePayment =
		details?.splitPaymentPlans && details.splitPaymentPlans.length <= 0 && details?.missedPaymentDetails;

	useEffect(() => {
		const getPaymentCollectionOptions = async () => {
			try {
				if (
					payNowDetails.details?.missedPaymentDetails?.directDebitId &&
					payNowDetails.details?.missedPaymentDetails?.directDebitId !== ''
				) {
					const client = new PayNowClient(undefined, API);
					const result = await client.getPaymentCollectionOptions({
						defaultedDirectDebitId: payNowDetails.details?.missedPaymentDetails?.directDebitId,
					});
					dispatch(updatePayNowDetails({ collectionOptions: result }));
				}
			} catch (error) {
				console.error('Error getting payment collection options:', error);
				navigate(ROUTE_PATHS.error);
			}
		};

		if (payNowDetails.details && payNowDetails.details.status === PayNowStatus.SplitPaymentOverdue) {
			navigate(ROUTE_PATHS.paymentPlanOverdue, { replace: true });
			return;
		}

		if (payNowDetails.collectionOptions.length === 0) {
			getPaymentCollectionOptions().catch(console.error);
		}
	}, [dispatch]);

	return (
		<FullPageLoadingSplash
			loading={payNowDetails.collectionOptions.length === 0}
			message='Retrieving Balance Details...'
		>
			<Paper
				elevation={1}
				sx={{
					margin: 1,
					borderRadius: '8px',
					paddingBottom: 3,
				}}
			>
				<Fade in>
					<Container maxWidth='md' sx={{ padding: 3 }}>
						<PageTitle>Payment Details</PageTitle>
						<Box
							sx={(theme) => ({
								maxWidth: '100%',
								margin: 'auto',
								display: 'flex',
								flexDirection: 'column',
								[theme.breakpoints.down('md')]: {
									maxWidth: '100%',
								},
							})}
						>
							<Box my={2}>
								{hasOverDuePayment && (
									<MissingPaymentForm
										details={details}
										customerEmail={customerEmail ?? ''}
										paymentCollection={collectionOptions}
										scheme={policyDetails.scheme}
									/>
								)}
							</Box>
							<Button
								variant='contained'
								color='primary'
								onClick={() => navigate(ROUTE_PATHS.home)}
								startIcon={<Home />}
								size='large'
								fullWidth
								sx={(theme) => ({
									maxWidth: '60%',
									margin: 'auto',
									[theme.breakpoints.down('md')]: {
										maxWidth: '100%',
									},
								})}
							>
								Go Home
							</Button>
						</Box>
					</Container>
				</Fade>
			</Paper>
		</FullPageLoadingSplash>
	);
};

export default SplitPaymentAgreement;
